import React, { forwardRef, useMemo, useCallback } from 'react'
import { Box, TableRow, TableCell } from '@mui/material'
import { BaseBlockProps, ChartFormat } from '../types'
import { useChartData } from '../../../hooks'
import {
    getColorConfig,
    hasFieldColors,
    getUniqueDates,
    getGroupMode,
    getChartLayout,
    isReverseAxis,
    isLabelsEnabled,
    isGridEnabled,
    isDateOriented,
    isPieChart,
    getBlockStyleConfig,
} from '../../../utils'

import {
    BlockWrapper,
    BlockHeader,
    BlockToolbar,
    LoadingState,
    ChartContainer,
    DataTable,
} from '../base'
import { PieChart, BarChart, LineChart } from '../../../Components/Display'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CountListBlockProps extends BaseBlockProps {}

export const CountListBlock = forwardRef<HTMLDivElement, CountListBlockProps>(
    (
        {
            kedo,
            block,
            dashboard,
            container,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            link,
        },
        ref
    ) => {
        const {
            rawData,
            transformedData,
            listField,
            loading,
            dateFormat,
            setDateFormat,
            hasColors,
            refetch,
        } = useChartData({
            kedo,
            block,
            dossier,
            link,
        })

        const handleFilterChange = useCallback(
            (key: string, value: string) => {
                if (key === 'dateFormat') {
                    setDateFormat(value as 'year_month' | 'quarter' | 'year')
                }
            },
            [setDateFormat]
        )

        const chartFormat = block.settings?.chart_format as ChartFormat
        const isDate = isDateOriented(block)
        const isPie = isPieChart(block)
        const colorConfig = getColorConfig(block.settings?.scheme, hasColors)
        const { containerStyle, className } = getBlockStyleConfig(
            dashboard,
            container,
            isPie,
            chartFormat,
            rawData?.length ?? 0
        )

        const renderDateTable = () => {
            if (!transformedData || !rawData) return null

            const dates = getUniqueDates(
                rawData as Array<{ yearAnddate?: string | null }>
            )
            const tableData = transformedData.tableData as Array<{
                id: string
                data: Array<{ x: string | number; y: number }>
            }>

            const columns = [
                { id: 'period', label: kedo.t('Period') },
                ...dates.map((date) => ({ id: date, label: date })),
                { id: 'other', label: kedo.t('Other') },
                { id: 'total', label: kedo.t('Total') },
            ]

            const processedData = tableData.map((item) => {
                const row: Record<string, unknown> = { period: item.id }
                dates.forEach((date) => {
                    const found = item.data.find((d) => d.x === date)
                    row[date] = found?.y ?? 0
                })
                const other = item.data.find((d) => d.x === 'other')
                row.other = other?.y ?? 0
                row.total = item.data.reduce((sum, d) => sum + d.y, 0)
                return row
            })

            return <DataTable columns={columns} data={processedData} />
        }

        const renderSimpleTable = () => {
            if (!transformedData) return null

            const columns = [
                { id: 'id', label: kedo.t('Name') },
                { id: 'value', label: kedo.t('Amount') },
            ]

            const total = transformedData.pieData.reduce(
                (sum, item) => sum + item.value,
                0
            )

            const footer = (
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>
                        {kedo.t('Total')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{total}</TableCell>
                </TableRow>
            )

            return (
                <DataTable
                    columns={columns}
                    data={
                        transformedData.pieData as unknown as Record<
                            string,
                            unknown
                        >[]
                    }
                    footer={footer}
                />
            )
        }

        const renderChart = useMemo(() => {
            if (!transformedData || !rawData || rawData.length === 0) {
                return null
            }

            if (isDate) {
                if (chartFormat === 'table') {
                    return renderDateTable()
                }

                if (chartFormat === 'bar') {
                    return (
                        <BarChart
                            data={transformedData.barData}
                            keys={transformedData.keys}
                            colors={colorConfig}
                            groupMode={getGroupMode(block.settings)}
                            layout={getChartLayout(block.settings)}
                            reverse={isReverseAxis(block.settings)}
                            enableLabels={isLabelsEnabled(block.settings)}
                            enableGrid={isGridEnabled(block.settings)}
                            legendDirection={
                                block.settings?.legend_direction ??
                                'left-to-right'
                            }
                            kedo={kedo}
                        />
                    )
                }

                if (chartFormat === 'pie') {
                    return (
                        <PieChart
                            data={transformedData.pieData}
                            colors={colorConfig}
                        />
                    )
                }

                return (
                    <LineChart
                        data={transformedData.lineData}
                        colors={colorConfig}
                        kedo={kedo}
                    />
                )
            }

            if (chartFormat === 'table') {
                return renderSimpleTable()
            }

            return (
                <PieChart data={transformedData.pieData} colors={colorConfig} />
            )
        }, [
            transformedData,
            rawData,
            isDate,
            chartFormat,
            colorConfig,
            block.settings,
            kedo,
        ])

        return (
            <BlockWrapper ref={ref} dashboard={dashboard} sx={containerStyle}>
                <BlockHeader>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={refetch}
                        setFilterAndFetchData={handleFilterChange}
                    />
                    {getBlockHeader?.(block, dossier)}
                </BlockHeader>
                {loading ? (
                    <LoadingState />
                ) : (
                    <ChartContainer height={containerStyle.height}>
                        {renderChart}
                    </ChartContainer>
                )}
            </BlockWrapper>
        )
    }
)

CountListBlock.displayName = 'CountListBlock'
